import React, { useState, useCallback, useRef } from 'react';
import { useCanvasAnimation } from '../../../hooks/useCanvasAnimation';

export function PulsarBeamSweep() {
  const [spinRate, setSpinRate] = useState<number>(3.0); // rad/sec
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [pulseCount, setPulseCount] = useState<number>(0);

  const beamAngleRef = useRef<number>(0);
  const wasInsideCrossingRef = useRef<boolean>(false);
  const earthFlashTimerRef = useRef<number>(0);

  // Play audio blip using Web Audio API
  const playAudioBlip = () => {
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;
      const audioCtx = new AudioContextClass();

      const osc = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, audioCtx.currentTime); // 880Hz sound

      // Keep it soft so it doesn't jar the user
      gainNode.gain.setValueAtTime(0.06, audioCtx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 0.08);

      osc.connect(gainNode);
      gainNode.connect(audioCtx.destination);

      osc.start();
      osc.stop(audioCtx.currentTime + 0.08);
    } catch (err) {
      console.warn('Audio Context failed:', err);
    }
  };

  const draw = useCallback((ctx: CanvasRenderingContext2D, deltaTime: number, totalTime: number) => {
    const width = ctx.canvas.clientWidth;
    const height = ctx.canvas.clientHeight;

    const cx = width / 2;
    const cy = height / 2 - 10;

    // Clear background
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, width, height);

    // 1. UPDATE ROTATION ANGLE
    if (!isPaused) {
      beamAngleRef.current = (beamAngleRef.current + deltaTime * spinRate) % (Math.PI * 2);
    }

    const currentAngle = beamAngleRef.current;

    // 2. CHECK PULSE CROSSING (Earth is at cx + 210, cy -> angle 0)
    // Checks if either beam (pointing at currentAngle or currentAngle + PI) aligns with Earth (angle 0)
    const normalizedAngle1 = currentAngle;
    const normalizedAngle2 = (currentAngle + Math.PI) % (Math.PI * 2);

    // Determine distance to 0 (Earth direction)
    const dist1 = Math.min(normalizedAngle1, Math.PI * 2 - normalizedAngle1);
    const dist2 = Math.min(normalizedAngle2, Math.PI * 2 - normalizedAngle2);
    
    const isCrossing = dist1 < 0.085 || dist2 < 0.085;

    if (isCrossing) {
      if (!wasInsideCrossingRef.current) {
        setPulseCount((prev) => prev + 1);
        earthFlashTimerRef.current = 0.15; // flash for 150ms
        playAudioBlip();
        wasInsideCrossingRef.current = true;
      }
    } else {
      wasInsideCrossingRef.current = false;
    }

    // Decay Earth flash timer
    earthFlashTimerRef.current = Math.max(0, earthFlashTimerRef.current - deltaTime);

    // 3. DRAW SWEEPING BEAMS (Lighthouse beams)
    const drawBeam = (angle: number) => {
      const beamRadius = 200;
      const spread = 0.14; // beam thickness in radians

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.arc(cx, cy, beamRadius, angle - spread, angle + spread);
      ctx.closePath();

      // Create radial fade gradient
      const bGrad = ctx.createRadialGradient(cx, cy, 10, cx, cy, beamRadius);
      bGrad.addColorStop(0, 'rgba(255, 255, 255, 0.7)');
      bGrad.addColorStop(0.2, 'rgba(251, 191, 36, 0.4)'); // gold/yellow
      bGrad.addColorStop(1, 'rgba(251, 191, 36, 0)');
      
      ctx.fillStyle = bGrad;
      ctx.fill();
      ctx.restore();
    };

    drawBeam(currentAngle);
    drawBeam(currentAngle + Math.PI);

    // 4. DRAW PULSAR (Core)
    const pulseRadius = 12 + Math.sin(totalTime * 12.0) * 1.5;
    
    // Core glow
    const pGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, pulseRadius * 2.2);
    pGlow.addColorStop(0, '#FFFFFF');
    pGlow.addColorStop(0.3, '#60A5FA'); // blue
    pGlow.addColorStop(1, 'rgba(96, 165, 250, 0)');
    ctx.fillStyle = pGlow;
    ctx.beginPath();
    ctx.arc(cx, cy, pulseRadius * 2.2, 0, Math.PI * 2);
    ctx.fill();

    // Central core sphere
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(cx, cy, 6, 0, Math.PI * 2);
    ctx.fill();

    // 5. DRAW EARTH (Edge of canvas)
    const earthX = cx + 210;
    const earthY = cy;
    const isEarthFlashing = earthFlashTimerRef.current > 0;

    // Outer glow for Earth
    if (isEarthFlashing) {
      const eGlow = ctx.createRadialGradient(earthX, earthY, 0, earthX, earthY, 24);
      eGlow.addColorStop(0, 'rgba(255, 255, 255, 0.8)');
      eGlow.addColorStop(1, 'rgba(255, 255, 255, 0)');
      ctx.fillStyle = eGlow;
      ctx.beginPath();
      ctx.arc(earthX, earthY, 24, 0, Math.PI * 2);
      ctx.fill();
    }

    // Earth Sphere
    ctx.fillStyle = isEarthFlashing ? '#FFFFFF' : '#22C55E';
    ctx.beginPath();
    ctx.arc(earthX, earthY, 8, 0, Math.PI * 2);
    ctx.fill();

    // Earth Label
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 9px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Earth 🌍', earthX, earthY - 14);

    // Counter HUD text
    ctx.fillStyle = '#22D3EE';
    ctx.font = 'bold 13px sans-serif';
    ctx.textAlign = 'left';
    ctx.fillText(`Pulses detected: ${pulseCount}`, 16, 56);

    // Simulation Title Header
    ctx.fillStyle = '#FFFFFF';
    ctx.font = 'bold 13px sans-serif';
    ctx.fillText('Pulsar Beam Sweep (Top-Down View)', 16, 26);

    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.font = '10px sans-serif';
    ctx.fillText('Beams sweep past Earth, triggering radio pulses', 16, 42);
  }, [spinRate, isPaused, pulseCount]);

  const canvasRef = useCanvasAnimation(draw, { height: 340 });

  return (
    <div className="relative w-full h-[340px] rounded-xl overflow-hidden bg-[#0F172A] border border-white/10 select-none">
      <canvas ref={canvasRef} className="block w-full h-full" />

      {/* Control Overlay */}
      <div className="absolute bottom-3 inset-x-3 bg-[#0b0f19]/80 backdrop-blur-sm p-3 rounded-lg border border-white/10 flex items-center gap-4">
        {/* Slider */}
        <div className="flex-1 flex flex-col gap-1">
          <span className="text-white text-[9px] uppercase font-bold tracking-wider opacity-60">
            Spin Rate: {spinRate.toFixed(1)} rad/s — {(spinRate / (2 * Math.PI)).toFixed(1)} rotations/s
          </span>
          <input
            type="range"
            min="0.5"
            max="15"
            step="0.5"
            value={spinRate}
            onChange={(e) => setSpinRate(parseFloat(e.target.value))}
            className="w-full accent-cyan-400 cursor-pointer h-1.5 bg-white/20 rounded-lg appearance-none"
          />
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => setIsPaused(!isPaused)}
            className="px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 text-white text-xs font-bold transition-all border border-white/10 uppercase tracking-wide"
          >
            {isPaused ? '▶ Resume' : '⏸ Pause'}
          </button>
          <button
            onClick={() => setPulseCount(0)}
            className="px-3 py-1.5 rounded bg-red-500/20 hover:bg-red-500/35 text-red-300 text-xs font-bold transition-all border border-red-500/20 uppercase tracking-wide"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}
